/**
 * Token Service
 * Centralized token management for authentication
 */

import { storageKeys } from '../../config/app.config';

export interface TokenSet {
  accessToken: string;
  idToken: string;
  refreshToken?: string;
}

export interface UserAttributes {
  email?: string;
  sub?: string;
  tenantId?: string;
  providerType?: string;
  groups?: string[];
  [key: string]: unknown;
}

// SECURITY (Phase 2, SEC-09): access and ID tokens live only in memory.
// The refresh token is the only token persisted, in sessionStorage (per tab,
// cleared when the tab closes), so a reload can restore the session.
let memoryTokens: { accessToken: string | null; idToken: string | null } = {
  accessToken: null,
  idToken: null,
};

type TokenListener = (authenticated: boolean) => void;
const listeners = new Set<TokenListener>();

const notify = (): void => {
  const authenticated = !!(memoryTokens.accessToken && memoryTokens.idToken);
  listeners.forEach((listener) => listener(authenticated));
};

/**
 * Token service for managing authentication tokens
 */
export class TokenService {
  /**
   * Get the access token (memory only)
   */
  static getAccessToken(): string | null {
    return memoryTokens.accessToken;
  }

  /**
   * Get the ID token (memory only)
   */
  static getIdToken(): string | null {
    return memoryTokens.idToken;
  }

  /**
   * Get the refresh token from sessionStorage
   */
  static getRefreshToken(): string | null {
    try {
      return sessionStorage.getItem(storageKeys.refreshToken);
    } catch {
      return null;
    }
  }

  /**
   * Get all tokens
   */
  static getTokens(): TokenSet | null {
    const accessToken = this.getAccessToken();
    const idToken = this.getIdToken();
    const refreshToken = this.getRefreshToken();

    if (!accessToken || !idToken) {
      return null;
    }

    return {
      accessToken,
      idToken,
      refreshToken: refreshToken || undefined,
    };
  }

  /**
   * Save tokens: access/ID in memory, refresh token in sessionStorage
   */
  static saveTokens(tokens: Partial<TokenSet>): void {
    if (tokens.accessToken) {
      memoryTokens.accessToken = tokens.accessToken;
    }
    if (tokens.idToken) {
      memoryTokens.idToken = tokens.idToken;
    }
    if (tokens.refreshToken) {
      try {
        sessionStorage.setItem(storageKeys.refreshToken, tokens.refreshToken);
      } catch {
        // Storage unavailable: the session ends when the tab reloads
      }
    }
    notify();
  }

  /**
   * Clear all tokens
   */
  static clearTokens(): void {
    memoryTokens = { accessToken: null, idToken: null };
    try {
      sessionStorage.removeItem(storageKeys.refreshToken);
      // Remove any tokens left in localStorage by builds before Phase 2
      localStorage.removeItem(storageKeys.accessToken);
      localStorage.removeItem(storageKeys.idToken);
      localStorage.removeItem(storageKeys.refreshToken);
      localStorage.removeItem(storageKeys.userAttributes);
      localStorage.removeItem(storageKeys.tenantId);
      localStorage.removeItem(storageKeys.providerType);
    } catch {
      // Storage unavailable: nothing persisted to clear
    }
    notify();
  }

  /**
   * Subscribe to sign-in / sign-out changes. Returns an unsubscribe function.
   */
  static onChange(listener: TokenListener): () => void {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  }

  /**
   * Get user attributes from storage
   */
  static getUserAttributes(): UserAttributes | null {
    try {
      const attrs = localStorage.getItem(storageKeys.userAttributes);
      return attrs ? JSON.parse(attrs) : null;
    } catch {
      return null;
    }
  }

  /**
   * Save user attributes to storage
   */
  static saveUserAttributes(attributes: UserAttributes): void {
    try {
      localStorage.setItem(storageKeys.userAttributes, JSON.stringify(attributes));
      if (attributes.tenantId) {
        localStorage.setItem(storageKeys.tenantId, attributes.tenantId);
      }
      if (attributes.providerType) {
        localStorage.setItem(storageKeys.providerType, attributes.providerType);
      }
    } catch (error) {
      console.error('Unable to save user attributes:', error);
    }
  }

  /**
   * Get tenant ID
   */
  static getTenantId(): string | null {
    try {
      return localStorage.getItem(storageKeys.tenantId);
    } catch {
      return null;
    }
  }

  /**
   * Get provider type
   */
  static getProviderType(): string | null {
    try {
      return localStorage.getItem(storageKeys.providerType);
    } catch {
      return null;
    }
  }

  /**
   * Check if user is authenticated (has valid tokens)
   */
  static isAuthenticated(): boolean {
    const accessToken = this.getAccessToken();
    const idToken = this.getIdToken();
    return !!(accessToken && idToken);
  }

  /**
   * Decode JWT token payload (without verification)
   */
  static decodeToken(token: string): Record<string, unknown> | null {
    try {
      const parts = token.split('.');
      if (parts.length !== 3) {
        return null;
      }
      const payload = parts[1];
      const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
      return JSON.parse(decoded);
    } catch {
      return null;
    }
  }

  /**
   * Check if token is expired
   */
  static isTokenExpired(token: string): boolean {
    const decoded = this.decodeToken(token);
    if (!decoded || typeof decoded.exp !== 'number') {
      return true;
    }
    // Add 60 second buffer
    return Date.now() >= (decoded.exp * 1000) - 60000;
  }

  /**
   * Check if current access token is expired
   */
  static isAccessTokenExpired(): boolean {
    const token = this.getAccessToken();
    if (!token) {
      return true;
    }
    return this.isTokenExpired(token);
  }

  /**
   * Get token expiration time
   */
  static getTokenExpiration(token: string): Date | null {
    const decoded = this.decodeToken(token);
    if (!decoded || typeof decoded.exp !== 'number') {
      return null;
    }
    return new Date(decoded.exp * 1000);
  }

  /**
   * Get authorization header value
   */
  static getAuthorizationHeader(): string | null {
    const token = this.getAccessToken();
    return token ? `Bearer ${token}` : null;
  }
}

// Export singleton instance methods for convenience
export const getAccessToken = TokenService.getAccessToken.bind(TokenService);
export const getIdToken = TokenService.getIdToken.bind(TokenService);
export const getRefreshToken = TokenService.getRefreshToken.bind(TokenService);
export const saveTokens = TokenService.saveTokens.bind(TokenService);
export const clearTokens = TokenService.clearTokens.bind(TokenService);
export const isAuthenticated = TokenService.isAuthenticated.bind(TokenService);
export const getAuthorizationHeader = TokenService.getAuthorizationHeader.bind(TokenService);

export default TokenService;
